// 🧠 批次 20：分片上传（256KB 一片 + 4 片并发 + 断点续传 + 单片 5 次重试）
//
// 背景：陈哥实测上传 6.59MB 文件 449 秒只跑到 21% 然后失败 → 上行带宽约 28KB/s（极低）。
//       1MB 片在这种带宽下每片要 37 秒，一片网络抖动/超时整轮就崩。
//       改成 256KB 片（约 9 秒/片）+ 单片 5 次重试 + 断点续传（失败只补传缺的片，不从头来）。
//
// 后端配套端点（backend/app/routers/content.py）：
//   POST   /assets/upload-init            { filename, size }                        → { session_id }
//   GET    /assets/upload-init/{sid}                                           → { received:[idx], total }
//   POST   /assets/upload-chunk           form(session_id,index,total,data)       → { received }
//   POST   /assets/upload-finish          form(session_id,account_id,title)       → { ok,id,type,file_path,size }
//   DELETE /assets/upload-init/{sid}                                        → { ok }

import api from '../api'

const CHUNK = 512 * 1024    // 512KB 一片：28KB/s 上行约 18 秒/片，减少请求次数，降低网络抖动影响
const CONC = 2              // 2 片并发（减少网络压力，避免拥塞）
const RETRY = 10            // 单片最多重试 10 次（慢网络需要更多容错）
const CHUNK_TIMEOUT = 90000 // 单片请求 90s 上限：适应慢网络，避免过早超时
const SESSION_TTL = 6 * 3600 * 1000  // 本地会话记录 6 小时内有效（后端目录 1 小时过期，超了会自动重建）

const RESUME_KEY = 'zk_up_'

export function fmtMB(bytes) {
  if (!bytes) return '0 MB'
  const mb = bytes / 1048576
  return (mb >= 100 ? Math.round(mb) : mb.toFixed(1)) + ' MB'
}

function resumeKey(file) {
  return RESUME_KEY + file.name + '_' + file.size
}

function loadResume(file) {
  try {
    const raw = localStorage.getItem(resumeKey(file))
    if (!raw) return null
    const r = JSON.parse(raw)
    if (!r || !r.sid || !r.total || !r.chunk) return null
    if (Date.now() - (r.saved_at || 0) > SESSION_TTL) { localStorage.removeItem(resumeKey(file)); return null }
    if (r.chunk !== CHUNK || r.total !== Math.max(1, Math.ceil(file.size / CHUNK))) {
      localStorage.removeItem(resumeKey(file)); return null
    }
    return r
  } catch (e) { return null }
}

function saveResume(file, sid, total) {
  try { localStorage.setItem(resumeKey(file), JSON.stringify({ sid, total, chunk: CHUNK, saved_at: Date.now() })) } catch (e) { }
}

function clearResume(file) {
  try { localStorage.removeItem(resumeKey(file)) } catch (e) { }
}

/**
 * 分片上传文件到素材库。
 * 返回结构与原 /assets 一致：{ ok, id, type, file_path, size }
 * @param {File} file 要上传的文件
 * @param {Object} opts { account_id, title,
 *   onProgress(phase, pct, sizeTxt, done, total, extra)
 *     phase = 'upload' | 'resume' | 'merge'
 *     extra = { retry, total, sid, resumed }
 * }
 */
export async function uploadChunked(file, opts) {
  opts = opts || {}
  const total = Math.max(1, Math.ceil(file.size / CHUNK))
  const sizeTxt = fmtMB(file.size)
  let done = 0
  let sid
  let skip = new Set()   // 断点续传：已上传成功的片 index

  // 1) 复用上一轮的会话（断点续传），否则新建
  const prev = loadResume(file)
  if (prev) {
    try {
      const st = await api.get('/assets/upload-init/' + prev.sid, { timeout: 20000 })
      if (st && st.ok && Array.isArray(st.received)) {
        sid = prev.sid
        skip = new Set(st.received.filter(i => i >= 0 && i < total))
        done = skip.size
        if (opts.onProgress) opts.onProgress('resume', done / total, sizeTxt, done, total, { resumed: true, total, sid })
      }
    } catch (e) { /* 会话已过期 → 走新建 */ }
  }
  if (!sid) {
    const init = await api.post('/assets/upload-init', { filename: file.name, size: file.size }, { timeout: 30000 })
    sid = init && init.session_id
    if (!sid) throw new Error('上传会话创建失败，请重试')
  }
  saveResume(file, sid, total)

  // 2) 传片（跳过已完成的）
  const uploadOne = async (idx) => {
    const fd = new FormData()
    fd.append('session_id', sid)
    fd.append('index', idx)
    fd.append('total', total)
    fd.append('data', file.slice(idx * CHUNK, (idx + 1) * CHUNK), file.name)
    await api.post('/assets/upload-chunk', fd, { timeout: CHUNK_TIMEOUT })
    done++
    if (opts.onProgress) {
      opts.onProgress('upload', done / total, sizeTxt, done, total, { total, sid })
    }
  }

  const queue = []
  for (let i = 0; i < total; i++) if (!skip.has(i)) queue.push(i)

  const workers = []
  for (let w = 0; w < Math.min(CONC, Math.max(1, queue.length)); w++) {
    workers.push((async () => {
      while (queue.length) {
        const idx = queue.shift()
        if (idx === undefined) break
        let ok = false
        for (let retry = 0; retry < RETRY && !ok; retry++) {
          try {
            await uploadOne(idx)
            ok = true
          } catch (e) {
            if (opts.onProgress) {
              opts.onProgress('upload', done / total, sizeTxt, done, total,
                { retry: retry + 1, maxRetry: RETRY, total, sid })
            }
            await new Promise(r => setTimeout(r, 3000 * (retry + 1)))
          }
        }
        if (!ok) {
          throw new Error('素材上传中断（第 ' + (idx + 1) + '/' + total + ' 片），已保留进度 ' +
            Math.round(done / total * 100) + '%，请重新上传（不会从头开始）')
        }
      }
    })())
  }
  await Promise.all(workers)

  // 3) 合并入库
  if (opts.onProgress) opts.onProgress('merge', 1, sizeTxt, total, total, { total, sid })
  const fdf = new FormData()
  fdf.append('session_id', sid)
  fdf.append('account_id', opts.account_id || 0)
  fdf.append('title', opts.title || '')
  const res = await api.post('/assets/upload-finish', fdf, { timeout: 120000 })
  clearResume(file)
  return res
}
